import {
  confirmResetPassword,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
  type ConfirmResetPasswordInput,
  type ConfirmSignUpInput,
  type ResetPasswordInput,
  type ResetPasswordOutput,
  type ResendSignUpCodeInput,
  type SignInInput,
  type SignInOutput,
  type SignUpInput,
  type SignUpOutput
} from 'aws-amplify/auth'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

const STORAGE_RETURN_TO = 'auth_return_to'

interface AuthContextValue {
  isAuthenticated: boolean
  sub: string | null
  email: string | null
  accessToken: string | null
  loading: boolean
  login: (returnTo?: string, mode?: 'sign-in' | 'sign-up') => void
  logout: () => Promise<void>
  signInWithPassword: (input: SignInInput) => Promise<SignInOutput>
  signUpWithPassword: (input: SignUpInput) => Promise<SignUpOutput>
  confirmUserSignUp: (input: ConfirmSignUpInput) => Promise<void>
  resendConfirmationCode: (input: ResendSignUpCodeInput) => Promise<void>
  requestPasswordReset: (input: ResetPasswordInput) => Promise<ResetPasswordOutput>
  confirmPasswordReset: (input: ConfirmResetPasswordInput) => Promise<void>
  consumeReturnTo: (fallback?: string) => string
  setReturnTo: (returnTo: string) => void
}

function missingAuthProvider(): never {
  throw new Error('AuthProvider is not mounted')
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  sub: null,
  email: null,
  accessToken: null,
  loading: true,
  login: () => {},
  logout: async () => missingAuthProvider(),
  signInWithPassword: async () => missingAuthProvider(),
  signUpWithPassword: async () => missingAuthProvider(),
  confirmUserSignUp: async () => missingAuthProvider(),
  resendConfirmationCode: async () => missingAuthProvider(),
  requestPasswordReset: async () => missingAuthProvider(),
  confirmPasswordReset: async () => missingAuthProvider(),
  consumeReturnTo: fallback => fallback ?? '/dashboard',
  setReturnTo: () => {}
})

function getAccessToken(session: Awaited<ReturnType<typeof fetchAuthSession>>): string | null {
  return session.tokens?.accessToken?.toString() ?? null
}

function clearSessionState(
  setAccessToken: (value: string | null) => void,
  setSub: (value: string | null) => void,
  setEmail: (value: string | null) => void
) {
  setAccessToken(null)
  setSub(null)
  setEmail(null)
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [sub, setSub] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const syncSession = useCallback(async () => {
    try {
      const [session, user] = await Promise.all([fetchAuthSession(), getCurrentUser()])
      const claims = session.tokens?.idToken?.payload

      setAccessToken(getAccessToken(session))
      setSub((claims?.sub as string | undefined) ?? user.userId ?? null)
      setEmail((claims?.email as string | undefined) ?? null)
    } catch {
      clearSessionState(setAccessToken, setSub, setEmail)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void syncSession()
  }, [syncSession])

  const setReturnTo = useCallback((returnTo: string) => {
    sessionStorage.setItem(STORAGE_RETURN_TO, returnTo)
  }, [])

  const consumeReturnTo = useCallback((fallback = '/dashboard') => {
    const returnTo = sessionStorage.getItem(STORAGE_RETURN_TO) ?? fallback
    sessionStorage.removeItem(STORAGE_RETURN_TO)
    return returnTo
  }, [])

  const login = useCallback((returnTo = '/dashboard', mode: 'sign-in' | 'sign-up' = 'sign-in') => {
    setReturnTo(returnTo)
    const params = new URLSearchParams({ mode })
    window.location.assign(`/auth?${params.toString()}`)
  }, [setReturnTo])

  const logout = useCallback(async () => {
    await signOut()
    clearSessionState(setAccessToken, setSub, setEmail)
    window.location.assign('/')
  }, [])

  const signInWithPassword = useCallback(async (input: SignInInput) => {
    const result = await signIn(input)
    if (result.isSignedIn) {
      await syncSession()
    }
    return result
  }, [syncSession])

  const signUpWithPassword = useCallback((input: SignUpInput) => signUp(input), [])

  const confirmUserSignUp = useCallback(async (input: ConfirmSignUpInput) => {
    await confirmSignUp(input)
  }, [])

  const resendConfirmationCode = useCallback(async (input: ResendSignUpCodeInput) => {
    await resendSignUpCode(input)
  }, [])

  const requestPasswordReset = useCallback((input: ResetPasswordInput) => resetPassword(input), [])

  const confirmPasswordReset = useCallback(async (input: ConfirmResetPasswordInput) => {
    await confirmResetPassword(input)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!accessToken,
        sub,
        email,
        accessToken,
        loading,
        login,
        logout,
        signInWithPassword,
        signUpWithPassword,
        confirmUserSignUp,
        resendConfirmationCode,
        requestPasswordReset,
        confirmPasswordReset,
        consumeReturnTo,
        setReturnTo
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  return useContext(AuthContext)
}
